import React from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { splitRiskSignals } from 'utils/entityRiskSignals';

/**
 * Full-record drawer for a screening hit — parity with the standalone Entity
 * Screening tool's `entity-record-drawer` (EntityScreeningResultsTable.tsx). It
 * renders the complete OpenSearch `_source` document as an "Entity Details"
 * key/value list and supports prev/next navigation across the visible hits.
 *
 * It reuses the tool's global drawer classes from `index.css`
 * (`entity-record-drawer`, `drawer-kv-box`, …) so it looks identical, and keeps
 * its own compact copies of the field-formatting helpers so the Export Hub
 * doesn't reach into the tool's internals.
 */

export interface ScreeningHit {
  // Full OpenSearch document; may be a synthesized fallback for legacy screens.
  source: Record<string, any>;
  // Export-Hub extra: the linked party this hit was screened under.
  matched_for?: string;
}

interface Props {
  open: boolean;
  hits: ScreeningHit[];
  index: number;
  onNavigate: (_index: number) => void;
  onClose: () => void;
}

const getValue = (source: any, keys: string[], fallback = '—'): string => {
  const value = keys.map((k) => source?.[k]).find(Boolean);
  return (value as string) || fallback;
};

const hasDisplayValue = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.filter(Boolean).length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return String(value).trim() !== '';
};

const formatFieldValue = (value: any): string => {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value ?? '');
};

// Turn http(s)/www tokens in a field value into links; keep the regex narrow
// since we render arbitrary record fields.
const URL_TOKEN_REGEX = /\b((?:https?:\/\/|www\.)[^\s<>"'`]+)/gi;

const renderFieldWithLinks = (value: string): React.ReactNode => {
  if (!value) return value;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  for (const match of value.matchAll(URL_TOKEN_REGEX)) {
    const url = match[0];
    const start = match.index ?? 0;
    if (start > lastIndex) parts.push(value.slice(lastIndex, start));
    let cleaned = url;
    let trailing = '';
    while (cleaned.length > 0 && '.,);:!?'.includes(cleaned[cleaned.length - 1])) {
      trailing = cleaned[cleaned.length - 1] + trailing;
      cleaned = cleaned.slice(0, -1);
    }
    const href = cleaned.startsWith('http') ? cleaned : `https://${cleaned}`;
    parts.push(
      <a
        key={`url-${key++}`}
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className='link'
      >
        {cleaned}
      </a>,
    );
    if (trailing) parts.push(trailing);
    lastIndex = start + url.length;
  }
  if (lastIndex < value.length) parts.push(value.slice(lastIndex));
  return parts.length > 0 ? parts : value;
};

const EntityRecordDrawer: React.FC<Props> = ({
  open,
  hits,
  index,
  onNavigate,
  onClose,
}) => {
  if (!open || index < 0 || index >= hits.length) return null;
  const hit = hits[index];
  const source = hit.source || {};
  const title = getValue(source, ['Name of Entity', 'Product Name', 'name']);
  const { signals: riskSignals, rest: details } = splitRiskSignals(source);

  return (
    <div className='drawer-backdrop' onClick={onClose}>
      <aside
        className='drawer-panel entity-record-drawer'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='drawer-header entity-record-drawer-header'>
          <div className='entity-record-drawer-nav'>
            <button
              type='button'
              onClick={() => onNavigate(index - 1)}
              disabled={index <= 0}
              title='Open previous record'
            >
              <ChevronLeftIcon sx={{ fontSize: 22 }} />
            </button>
            <button
              type='button'
              onClick={() => onNavigate(index + 1)}
              disabled={index >= hits.length - 1}
              title='Open next record'
            >
              <ChevronRightIcon sx={{ fontSize: 22 }} />
            </button>
          </div>
          <h2 className='drawer-title entity-record-drawer-title'>
            <span className='entity-record-drawer-title-main'>{title}</span>
          </h2>
          <button
            type='button'
            className='drawer-close-btn entity-record-drawer-close'
            onClick={onClose}
          >
            <CloseIcon sx={{ fontSize: 28 }} />
          </button>
        </div>
        <div className='drawer-body entity-record-drawer-body'>
          {riskSignals.length > 0 && (
            <div>
              <div className='drawer-section-title'>Risk Signals</div>
              <div className='drawer-kv-box entity-record-drawer-kv-box'>
                {riskSignals.map(({ label, value }) => (
                  <div
                    className='drawer-kv-row entity-record-drawer-row'
                    key={label}
                  >
                    <span className='drawer-kv-label'>{label}</span>
                    <span className='drawer-kv-value'>
                      {renderFieldWithLinks(formatFieldValue(value))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className='drawer-section-title'>Entity Details</div>
            <div className='drawer-kv-box entity-record-drawer-kv-box'>
              {hit.matched_for && (
                <div className='drawer-kv-row entity-record-drawer-row'>
                  <span className='drawer-kv-label'>Matched For</span>
                  <span className='drawer-kv-value'>{hit.matched_for}</span>
                </div>
              )}
              {Object.entries(details)
                .filter(([, value]) => hasDisplayValue(value))
                .map(([label, value]) => (
                  <div
                    className='drawer-kv-row entity-record-drawer-row'
                    key={label}
                  >
                    <span className='drawer-kv-label'>{label}</span>
                    <span className='drawer-kv-value'>
                      {renderFieldWithLinks(formatFieldValue(value))}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default EntityRecordDrawer;
